import { ComegoTime } from "@timeghost/web-api";
import { areIntervalsOverlapping, isValid } from "date-fns/esm";

export type AbsenceConflictInterval = {
	dayKey: string;
	start: Date;
	end: Date;
};

export type AbsenceConflictRange = {
	from: Date;
	to: Date;
};

function workingHourOverlapsInterval(wh: ComegoTime, intervalStart: Date, intervalEnd: Date): boolean {
	if (!wh.start || !wh.end || !wh.id) return false;
	const whStart = new Date(wh.start);
	const whEnd = new Date(wh.end);
	if (!isValid(whStart) || !isValid(whEnd) || whStart >= whEnd) return false;

	return (
		areIntervalsOverlapping({ start: intervalStart, end: intervalEnd }, { start: whStart, end: whEnd }) &&
		!(intervalEnd.getTime() === whStart.getTime() || whEnd.getTime() === intervalStart.getTime())
	);
}

export function computeAbsenceConflicts(
	workingTimes: ComegoTime[],
	absenceIntervalsByUser: Map<string, AbsenceConflictInterval[]>
): { conflicts: Map<string, Set<string>>; relevantWorkingHours: ComegoTime[] } {
	const conflicts = new Map<string, Set<string>>();
	const relevantWorkingHours: ComegoTime[] = [];

	for (const wh of workingTimes) {
		const intervals = absenceIntervalsByUser.get(wh.user.id);
		if (!intervals?.length) continue;

		let hasOverlap = false;
		for (const interval of intervals) {
			if (!workingHourOverlapsInterval(wh, interval.start, interval.end)) continue;
			hasOverlap = true;
			conflicts.set(wh.user.id, conflicts.get(wh.user.id) ?? new Set());
			conflicts.get(wh.user.id)!.add(interval.dayKey);
		}
		if (hasOverlap) {
			relevantWorkingHours.push(wh);
		}
	}

	return { conflicts, relevantWorkingHours };
}

export function cloneAbsenceIntervalsByUser(
	source: Map<string, AbsenceConflictInterval[]>
): Map<string, AbsenceConflictInterval[]> {
	return new Map(
		[...source.entries()].map(([userId, intervals]) => [
			userId,
			intervals.map((interval) => ({
				dayKey: interval.dayKey,
				start: new Date(interval.start),
				end: new Date(interval.end),
			})),
		])
	);
}

export function cloneConflictsMap(source: Map<string, Set<string>>): Map<string, Set<string>> {
	return new Map([...source.entries()].map(([userId, days]) => [userId, new Set(days)]));
}
